// src/components/modules/ListingApp/components/ListingTable.js
import React, { useState, useRef } from "react";
import StickyListingScrollbar from "./StickyListingScrollbar";
import ConvertToAuctionModal from "./ConvertToAuctionModal";
import { convertToAuction, printLabel } from "../services/listingApi";
import { Button, Chip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { formatDateTime } from "../constants/listingConstants";


const ListingTable = ({
  rows = [],
  handleOpenEditDialog,
  handleSync,
  handleEnd,
  handleListOnEbay,
  handleScrapeMetadata,
  handleScrapePrice,
  handleApplyLatestScrapedPrice,
  handlePriceOnlyUpdate,
  handleMarkAsTrainer,
  refresh,
  onSelectionChange,
}) => {
  const gridRef = useRef(null);
  // MUI X v8 row selection model: { type: "include" | "exclude", ids: Set }
  const [selectionModel, setSelectionModel] = useState({
    type: "include",
    ids: new Set(),
  });

  const [auctionModalOpen, setAuctionModalOpen] = useState(false);
  const [auctionListing, setAuctionListing] = useState(null);


const handleOpenConvertToAuction = (row) => {
  setAuctionListing(row);
  setAuctionModalOpen(true);
};

const handleConfirmConvertToAuction = async ({ listing_duration, start_price }) => {
  const listingId = auctionListing?.id;
  if (!listingId) throw new Error("No listing selected");
  await convertToAuction(Number(listingId), { listing_duration, start_price });
  refresh?.();
};

  const handlePrintLabel = async (row) => {
    const listingId = row?.id;
    if (!listingId) return;
    try {
      await printLabel(Number(listingId), {
        card_name: row?.card_name || "",
        card_number: row?.card_number || row?.cardNumber || "",
      });
      refresh?.();
    } catch (err) {
      alert(`Print failed: ${err?.message || "Unknown error"}`);
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "sku", headerName: "SKU", width: 140 },
    { field: "purchase_order_id", headerName: "PO", width: 110 },
    { field: "card_name", headerName: "Card Name", width: 220 },

    // Finance fields - match Prod
    { field: "agreed_price", headerName: "Agreed Price", width: 130 },
    { field: "predicted_sale_price", headerName: "Predicted Price", width: 150 },
    { field: "predicted_sale_price_scraped", headerName: "Scraped Predicted", width: 170 },
    { field: "expected_profit", headerName: "Expected Profit", width: 150 },
    { field: "expected_margin", headerName: "Margin (%)", width: 120 },

    { field: "listing_title", headerName: "Listing Title", width: 420 },
    {
      field: "listing_format",
      headerName: "Format",
      width: 110,
      valueGetter: (value, row) => (row?.listing_format || row?.format || "FIXED_PRICE"),
    },
    {
      field: "listing_duration",
      headerName: "Duration",
      width: 110,
      valueGetter: (value, row) => (row?.listing_duration || row?.duration || ""),
    },
    { field: "listing_condition", headerName: "Condition", width: 160 },

    // Just show whatever backend sends (e.g. "110.00") - same as Prod
    {
      field: "listing_price",
      headerName: "Listing Price",
      width: 130,
    },
    {
      field: "display_quantity",
      headerName: "Quantity",
      width: 100,
      valueGetter: (value, row) => row?.display_quantity ?? row?.ebay_available_quantity ?? 0,
    },
    {
      field: "trainer_inventory_listing_type",
      headerName: "Listing Type",
      width: 120,
      valueGetter: (value, row) => {
        const listingType = row?.trainer_inventory_listing_type;
        if (listingType === "playset") return "Playset";
        if (listingType === "single") return "Single";
        return "";
      },
    },
    {
      field: "trainer_inventory_link",
      headerName: "Trainer Link",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (p) => {
        const row = p?.row ?? {};
        if (row?.trainer_inventory_playset_id) {
          return <Chip size="small" color="secondary" label={`Trainer #${row.trainer_inventory_playset_id}`} />;
        }
        if (row?.trainer_inventory_single_id) {
          return <Chip size="small" color="primary" label={`Trainer #${row.trainer_inventory_single_id}`} />;
        }
        return "";
      },
    },

    {
      field: "change_details",
      headerName: "Change Details",
      width: 800,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <>
          <Button onClick={() => handleOpenEditDialog?.(params?.row)}>
            CHANGE DETAILS
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleScrapePrice?.(params?.row?.id)}
          >
            SCRAPE PRICE
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleApplyLatestScrapedPrice?.(params?.row?.id)}
          >
            APPLY SCRAPED
          </Button>
          <Button
            variant="contained"
            onClick={() => handleListOnEbay?.(params?.row?.id)}
          >
            LIST
          </Button>
          <Button
            variant="outlined"
            onClick={() => handlePriceOnlyUpdate?.(params?.row?.id)}
          >
            Change Price (eBay Only)
          </Button>
        </>
      ),
    },

    {
      field: "listing_status",
      headerName: "Status",
      width: 140,
      renderCell: (p) => (
        <Chip size="small" label={p?.row?.listing_status || ""} />
      ),
    },
    {
      field: "ebay_listing_status",
      headerName: "eBay Status",
      width: 140,
      renderCell: (p) => (
        <Chip size="small" label={p?.row?.ebay_listing_status || ""} />
      ),
    },
    {
      field: "created_at",
      headerName: "Created At",
      width: 160,
      valueGetter: (p) => formatDateTime(p?.row?.created_at),
    },
    {
      field: "updated_at",
      headerName: "Updated At",
      width: 160,
      valueGetter: (p) => formatDateTime(p?.row?.updated_at),
    },
    {
      field: "scrape_metadata",
      headerName: "Scrape Metadata",
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: (p) =>
        !p?.row?.rarity || !p?.row?.card_set ? (
          <Button
            variant="outlined"
            onClick={() => handleScrapeMetadata?.(p?.row?.card_name)}
          >
            SCRAPE
          </Button>
        ) : (
          ""
        ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 470,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params?.row ?? {};
        // Print Label should always be available (backend will validate any constraints).
        const canPrint = !!row.id;
        return (
          <>
            <Button disabled={!canPrint} onClick={() => handlePrintLabel(row)}>
              PRINT LABEL
            </Button>
            <Button onClick={() => handleSync?.(row.id)}>SYNC</Button>
            <Button onClick={() => handleEnd?.(row.id)}>END</Button>
            <Button
              variant={row?.is_trainer_inventory_linked ? "contained" : "outlined"}
              onClick={() => handleMarkAsTrainer?.(row)}
            >
              {row?.is_trainer_inventory_linked ? "TRAINER LINKED" : "MARK TRAINER"}
            </Button>
            <Button
              disabled={
                String(row.listing_format || "").toUpperCase() !== "FIXED_PRICE" ||
                ["Sold", "Ended"].includes(row.listing_status) ||
                !!row.replaced_by_listing_id
              }
              onClick={() => handleOpenConvertToAuction(row)}
            >
              CONVERT
            </Button>
          </>
        );
      },
    },
  ];

  return (
    <>
      <div ref={gridRef}>
      <DataGrid
      autoHeight
      rows={rows || []}
      columns={columns}
      getRowId={(row) => row?.id}
      pageSize={100}
      rowsPerPageOptions={[100, 500]}
      disableRowSelectionOnClick
      checkboxSelection
      rowSelectionModel={selectionModel}
      onRowSelectionModelChange={(newModel) => {
        setSelectionModel(newModel);
        onSelectionChange?.(newModel);
      }}
    />
      </div>
      <StickyListingScrollbar gridRef={gridRef} />

      <ConvertToAuctionModal
        open={auctionModalOpen}
        listing={auctionListing}
        onClose={() => setAuctionModalOpen(false)}
        onConfirm={handleConfirmConvertToAuction}
      />
    </>
  );
};

export default ListingTable;
